/**
 * SubscriptionHistoryDialog — Abo-Timeline
 *
 * Iter 06/2026 (User-Wunsch Markus): Neben verbrauchten Terminen jetzt
 * auch die zugehörigen Rechnungen anzeigen. Pro Termin wird Trainer +
 * Status (verbraucht / cancelled / no_show) angezeigt.
 *
 * Backend liefert via `/api/subscriptions/{id}/timeline`:
 *   { subscription, invoices[], appointments[], summary }
 *
 * Backwards-compat: Wenn das alte Schema `appointments_used` ankommt
 * (z.B. von einem alten Caller), normalisieren wir hier.
 */
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { History, Calendar, Receipt, User, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('de-CH', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  } catch {
    return iso;
  }
};

const formatCHF = (amount) => {
  const n = Number(amount || 0);
  return `CHF ${n.toFixed(2)}`;
};

// Status-Badge inkl. Farbe — User-Wahl Option b: Datum + Service + Trainer + Status
const StatusBadge = ({ status }) => {
  const s = (status || 'unknown').toLowerCase();
  if (['completed', 'checked_in', 'used', 'scheduled', 'confirmed'].includes(s)) {
    return (
      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-emerald-200">
        <CheckCircle2 className="h-3 w-3 mr-1" /> verbraucht
      </Badge>
    );
  }
  if (s === 'cancelled' || s === 'canceled') {
    return (
      <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100 border-rose-200">
        <XCircle className="h-3 w-3 mr-1" /> storniert
      </Badge>
    );
  }
  if (s === 'no_show' || s === 'noshow') {
    return (
      <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border-amber-200">
        <AlertCircle className="h-3 w-3 mr-1" /> no-show
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-xs">
      {s}
    </Badge>
  );
};

const SubscriptionHistoryDialog = ({
  open,
  onOpenChange,
  loadingHistory,
  subscriptionHistory,
}) => {
  // Normalisierung: bei altem Schema (`appointments_used`) auf das neue Feld mappen.
  const appointments =
    subscriptionHistory?.appointments
    || (subscriptionHistory?.appointments_used || []).map((a) => ({
      appointment_id: a.appointment_id,
      date: a.date,
      start_time: a.start_time,
      service_name: a.service_name,
      trainer_name: a.trainer_name,
      status: a.status || 'completed',
      credits_used: a.points_cost || a.credits_used || 1,
    }));
  const invoices = subscriptionHistory?.invoices || [];
  const sub = subscriptionHistory?.subscription;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="subscription-timeline-dialog"
        aria-describedby="subscription-timeline-desc"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History size={20} />
            Abo-Übersicht
          </DialogTitle>
        </DialogHeader>
        <p id="subscription-timeline-desc" className="sr-only">
          Übersicht: Verknüpfte Rechnungen und mit dem Abo verbrauchte Termine.
        </p>

        {loadingHistory ? (
          <div className="text-center py-8 text-sm text-zinc-500">Laden…</div>
        ) : sub ? (
          <div className="space-y-6">
            {/* Abo-Header */}
            <div
              className="p-4 bg-emerald-50 rounded-lg border border-emerald-200"
              data-testid="subscription-timeline-header"
            >
              <h3 className="font-semibold text-emerald-900 mb-2">{sub.name}</h3>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div>
                  <p className="text-emerald-700 text-xs">Gesamt</p>
                  <p className="text-xl font-bold text-emerald-900">{sub.credits_total}</p>
                </div>
                <div>
                  <p className="text-emerald-700 text-xs">Verbraucht</p>
                  <p className="text-xl font-bold text-emerald-900">{sub.credits_used}</p>
                </div>
                <div>
                  <p className="text-emerald-700 text-xs">Verbleibend</p>
                  <p className="text-xl font-bold text-emerald-900">{sub.credits_remaining}</p>
                </div>
              </div>
              <div className="w-full bg-emerald-200 rounded-full h-2 mt-3">
                <div
                  className="bg-emerald-600 h-2 rounded-full transition-all"
                  style={{
                    width: `${
                      sub.credits_total > 0
                        ? (sub.credits_remaining / sub.credits_total) * 100
                        : 0
                    }%`,
                  }}
                />
              </div>
            </div>

            {/* Zugehörige Rechnungen */}
            <div data-testid="subscription-timeline-invoices">
              <h4 className="font-semibold text-zinc-900 mb-3 flex items-center gap-2">
                <Receipt size={16} />
                Bezahlt mit Rechnung{invoices.length === 1 ? '' : 'en'} ({invoices.length})
              </h4>
              {invoices.length > 0 ? (
                <div className="space-y-2">
                  {invoices.map((inv) => (
                    <div
                      key={inv.id}
                      className="p-3 bg-zinc-50 rounded-lg border flex justify-between items-center"
                      data-testid={`subscription-timeline-invoice-${inv.id}`}
                    >
                      <div>
                        <p className="font-medium text-zinc-900">{inv.invoice_number}</p>
                        <p className="text-xs text-zinc-500">
                          {formatDate(inv.issue_date)}
                          {inv.due_date ? ` · Fällig ${formatDate(inv.due_date)}` : ''}
                          {inv.paid_at ? ` · Bezahlt ${formatDate(inv.paid_at)}` : ''}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-zinc-900">{formatCHF(inv.total_amount)}</p>
                        <Badge
                          variant={inv.status === 'paid' ? 'default' : 'outline'}
                          className={
                            inv.status === 'paid'
                              ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-emerald-200'
                              : 'border-amber-300 text-amber-700'
                          }
                        >
                          {inv.status === 'paid' ? 'bezahlt' : inv.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-zinc-500 text-sm py-2">
                  Keine verknüpfte Rechnung gefunden.
                </p>
              )}
            </div>

            {/* Termine */}
            <div data-testid="subscription-timeline-appointments">
              <h4 className="font-semibold text-zinc-900 mb-3 flex items-center gap-2">
                <Calendar size={16} />
                Verbrauchte Termine ({appointments.length})
              </h4>
              {appointments.length > 0 ? (
                <div className="space-y-2">
                  {appointments.map((apt) => (
                    <div
                      key={apt.appointment_id}
                      className="p-3 bg-zinc-50 rounded-lg border"
                      data-testid={`subscription-timeline-apt-${apt.appointment_id}`}
                    >
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-zinc-900 truncate">
                            {apt.service_name || 'Unbekannt'}
                          </p>
                          <p className="text-xs text-zinc-500 mt-0.5">
                            {formatDate(apt.date)}
                            {apt.start_time ? ` · ${apt.start_time}` : ''}
                          </p>
                          {apt.trainer_name && (
                            <p className="text-xs text-zinc-600 mt-1 flex items-center gap-1">
                              <User className="h-3 w-3" /> {apt.trainer_name}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <StatusBadge status={apt.status} />
                          <span className="text-[10px] text-zinc-500">
                            −{apt.credits_used} Credit{apt.credits_used !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-zinc-500 text-sm py-4 text-center">
                  Noch keine Termine mit diesem Abo gebucht.
                </p>
              )}
            </div>

            {sub.status === 'exhausted' && (
              <div className="p-3 bg-zinc-100 rounded-lg border border-zinc-300">
                <p className="text-sm text-zinc-700">
                  ✅ Dieses Abo wurde vollständig aufgebraucht
                  {sub.exhausted_at && (
                    <span className="block text-xs text-zinc-500 mt-1">
                      am {formatDate(sub.exhausted_at)}
                    </span>
                  )}
                </p>
              </div>
            )}
          </div>
        ) : (
          <p className="text-center py-8 text-zinc-500">Keine Daten verfügbar</p>
        )}

        <div className="flex justify-end mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="subscription-timeline-close">
            Schliessen
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionHistoryDialog;
